import { api, ApiError } from "../api.js";
import { el } from "./dom.js";
import {
  DEFAULT_TEAM_COUNT,
  MAX_TEAM,
  RANDOM_MAP_ID,
  ROOM_NAME_MAX,
  ROOM_NAME_MIN,
  TEAM_COLORS,
  type GameListEntry,
  type MapInfo,
} from "@webolo/shared";

// id of the persistent TURBO World preset (mirrors server.ts PRESET_ROOMS).
// We hide its row from the list when the admin gate is OFF.
const TURBO_WORLD_ROOM_ID = "turbo-world";

export function gamesPanel(
  onPlay: (gameId: string, team: number) => void,
  opts: { omitSiege?: boolean } = {},
): HTMLElement {
  const list = el("div", { class: "rooms-list" });
  const errorEl = el("div", { class: "lcard-error" });

  // Cached on mount. When false: hide TURBO World from the list and the
  // STYLE dropdown from the create form. Pre-existing TURBO rooms a player
  // happened to be in still show their badge — only the discovery path is
  // gated.
  let turboEnabled = true;
  // When false: hide the SIEGE mode option, the always-on challenge row, and
  // any siege rooms (admin testing switch). The Custom Game panel inside the
  // new tile lobby pins this off via opts.omitSiege regardless of the admin
  // gate, since SIEGE has its own dedicated tile up top.
  let siegeEnabled = !opts.omitSiege;
  const forceHideSiege = !!opts.omitSiege;

  const nameInput = el("input", {
    type: "text",
    class: "lcard-input",
    maxlength: ROOM_NAME_MAX,
    placeholder: `${ROOM_NAME_MIN}–${ROOM_NAME_MAX} characters`,
  });

  const teamCountSelect = teamCountPicker();
  const mapSelect = mapPicker([]);
  const botCountSelect = botCountPicker();
  const styleSelect = stylePicker();
  // The MODE field wrapper — toggled hidden when neither TURBO nor SIEGE is
  // available (i.e. only CLASSIC remains).
  const styleField = field("MODE", styleSelect);
  const teamField = field("TEAMS", teamCountSelect);
  const createBtn = el("button", { type: "button", class: "lcard-create" }, "CREATE");

  // SIEGE is a one-team co-op survival mode — the team picker has no meaning,
  // so grey it out while SIEGE is selected.
  function syncTeamPickerForMode(): void {
    const siege = styleSelect.value === "siege";
    teamCountSelect.disabled = siege;
    teamField.classList.toggle("create-field-disabled", siege);
  }
  styleSelect.addEventListener("change", syncTeamPickerForMode);

  // Rebuild the MODE options for the current gates and keep the field's
  // visibility in sync. Called after the config fetch resolves.
  function syncModeOptions(): void {
    const prev = styleSelect.value;
    styleSelect.replaceChildren();
    const add = (value: string, label: string) => {
      const opt = document.createElement("option");
      opt.value = value;
      opt.textContent = label;
      styleSelect.append(opt);
    };
    add("classic", "CLASSIC");
    if (turboEnabled) add("turbo", "SUPER");
    if (siegeEnabled) add("siege", "SIEGE");
    // Restore previous selection if it's still available.
    if ([...styleSelect.options].some((o) => o.value === prev)) styleSelect.value = prev;
    // Only worth showing the dropdown when there's a choice beyond CLASSIC.
    styleField.style.display = styleSelect.options.length > 1 ? "" : "none";
    syncTeamPickerForMode();
  }

  async function refresh(): Promise<void> {
    try {
      const data = await api.listGames();
      renderList(data.games);
    } catch {
      list.replaceChildren(el("div", { class: "lcard-empty" }, "Failed to load games."));
    }
  }

  async function loadMaps(): Promise<void> {
    try {
      const data = await api.listMaps();
      populateMapPicker(mapSelect, data.maps);
    } catch {
      // Non-fatal — empty list means procedural starter only.
    }
  }

  async function loadConfigs(): Promise<void> {
    try {
      const { enabled } = await api.turboConfig();
      turboEnabled = enabled;
    } catch {
      // Default to enabled — admin gate is best-effort; if the endpoint
      // is unreachable, fall back to showing the feature.
      turboEnabled = true;
    }
    try {
      const { enabled } = await api.siegeConfig();
      siegeEnabled = enabled && !forceHideSiege;
    } catch {
      siegeEnabled = !forceHideSiege;
    }
    syncModeOptions();
    // Re-render the list with the new visibility so TURBO World / SIEGE rows
    // come or go without a full panel reload.
    void refresh();
  }

  function renderList(games: GameListEntry[]): void {
    let visible = games;
    if (!turboEnabled) visible = visible.filter((g) => g.id !== TURBO_WORLD_ROOM_ID);
    // When SIEGE is gated off, drop the challenge row and any siege rooms.
    if (!siegeEnabled) visible = visible.filter((g) => !g.siegeChallenge && g.style !== "siege");
    if (visible.length === 0) {
      list.replaceChildren(el("div", { class: "lcard-empty" }, "No games yet — create one!"));
      return;
    }
    // The synthetic SIEGE challenge always sorts to the very top.
    visible = [...visible].sort((a, b) => (b.siegeChallenge ? 1 : 0) - (a.siegeChallenge ? 1 : 0));
    list.replaceChildren(...visible.map((g) =>
      g.siegeChallenge ? renderSiegeChallengeRow(g, onJoinSiegeChallenge) : renderGameRow(g, onPlay),
    ));
  }

  // Spin up a fresh private siege room for this player and drop them straight
  // into it. The room is hidden from the lobby and torn down when they leave.
  async function onJoinSiegeChallenge(): Promise<void> {
    errorEl.textContent = "";
    try {
      const res = await api.createGame({
        name: "Siege Challenge",
        style: "siege",
        solo: true,
        mapId: RANDOM_MAP_ID,
      });
      onPlay(res.id, 1);
    } catch (e) {
      errorEl.textContent = e instanceof ApiError && e.message === "siege_disabled"
        ? "Siege is currently disabled."
        : "Failed to start the siege challenge.";
    }
  }

  createBtn.addEventListener("click", async () => {
    const name = nameInput.value.trim();
    errorEl.textContent = "";
    if (name.length < ROOM_NAME_MIN || name.length > ROOM_NAME_MAX) {
      errorEl.textContent = `Name must be ${ROOM_NAME_MIN}–${ROOM_NAME_MAX} characters.`;
      return;
    }
    const teamCount = parseInt(teamCountSelect.value, 10);
    const mapId = mapSelect.value;
    const botCount = parseInt(botCountSelect.value, 10) || 0;
    const sel = styleSelect.value;
    const style =
      sel === "turbo" && turboEnabled ? "turbo"
      : sel === "siege" && siegeEnabled ? "siege"
      : "classic";
    createBtn.setAttribute("disabled", "");
    try {
      const res = await api.createGame({ name, teamCount, mapId, botCount, style });
      nameInput.value = "";
      await refresh();
      onPlay(res.id, 0);
    } catch (e) {
      if (e instanceof ApiError && e.body && typeof e.body === "object" && "error" in e.body) {
        const code = (e.body as { error: string }).error;
        if (code === "room_limit_reached") errorEl.textContent = "Room limit reached.";
        else if (code === "invalid_input" || code === "invalid_name") errorEl.textContent = "Invalid name or team count.";
        else if (code === "unknown_map") errorEl.textContent = "Selected map is not available.";
        else if (code === "turbo_disabled") errorEl.textContent = "TURBO style is currently disabled.";
        else if (code === "siege_disabled") errorEl.textContent = "SIEGE mode is currently disabled.";
        else if (code === "not_authenticated") errorEl.textContent = "Session expired. Please log in again.";
        else errorEl.textContent = "Failed to create room.";
      } else {
        errorEl.textContent = "Failed to create room.";
      }
    } finally {
      createBtn.removeAttribute("disabled");
    }
  });

  syncModeOptions();
  void refresh();
  void loadMaps();
  void loadConfigs();
  const refreshInterval = window.setInterval(refresh, 3000);

  const wrap = el(
    "section",
    { class: "lcard lcard-games" },
    el(
      "header",
      { class: "lcard-head" },
      el("span", { class: "lcard-title" }, "GAMES"),
    ),
    el(
      "div",
      { class: "lcard-body" },
      el(
        "div",
        { class: "create-row" },
        field("ROOM NAME", nameInput),
        el(
          "div",
          { class: "create-opts" },
          teamField,
          field("MAP", mapSelect),
          field("BOTS", botCountSelect),
          styleField,
        ),
        createBtn,
      ),
      errorEl,
      list,
    ),
  );

  // Clean up interval when the panel is removed from the DOM.
  new MutationObserver((muts) => {
    for (const m of muts) {
      for (const r of Array.from(m.removedNodes)) {
        if (r === wrap || (r as HTMLElement).contains?.(wrap)) {
          clearInterval(refreshInterval);
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  return wrap;
}

function field(label: string, control: HTMLElement): HTMLElement {
  return el(
    "label",
    { class: "create-field" },
    el("span", { class: "create-field-lbl" }, label),
    control,
  );
}

// The always-on single-player SIEGE challenge. Looks like a normal lobby game
// but joining spins up a private run for this player.
function renderSiegeChallengeRow(
  g: GameListEntry,
  onJoin: () => void,
): HTMLElement {
  const defenders = g.siegeDefenders ?? 0;
  const nameCol = el(
    "div",
    { class: "rn-info" },
    el("div", { class: "rn-title" },
      "SIEGE",
      el("span", { class: "room-badge room-badge-siege" }, "SOLO"),
    ),
    el("div", { class: "rn-sub" },
      "Solo challenge — how many waves of tanks can you survive?"),
    el("div", { class: "rn-sub rn-sub-live" },
      `${defenders} ${defenders === 1 ? "player is" : "players are"} defending their own island right now`),
  );
  const playBtn = el(
    "button",
    { class: "join-btn join-btn-siege", onclick: onJoin },
    "PLAY",
  );
  return el("div", { class: "room-row room-row-siege" }, nameCol, el("div", { class: "rn-count" }), playBtn);
}

function renderGameRow(
  g: GameListEntry,
  onPlay: (gameId: string, team: number) => void,
): HTMLElement {
  const full = g.players >= g.maxPlayers;
  const isSiege = g.style === "siege";
  const modeLabel = isSiege ? "Co-op survival" : g.teamCount === 0 ? "Free-for-all" : `${g.teamCount} teams`;
  const mapLabel = g.mapName ? ` · ${g.mapName}` : "";
  // Bot count is intentionally omitted from the room meta so the lobby
  // reads as livelier to newcomers (bots count toward the player tally).

  const titleChildren: (HTMLElement | string)[] = [g.name];
  if (g.style === "turbo") {
    titleChildren.push(el("span", { class: "room-badge room-badge-turbo" }, "SUPER"));
  } else if (isSiege) {
    titleChildren.push(el("span", { class: "room-badge room-badge-siege" }, "SIEGE"));
  }
  const nameCol = el(
    "div",
    { class: "rn-info" },
    el("div", { class: "rn-title" }, ...titleChildren),
    el("div", { class: "rn-sub" }, `${modeLabel}${mapLabel}`),
  );

  const countCol = el(
    "div",
    { class: "rn-count" },
    String(g.players),
    el("span", { class: "rn-count-max" }, `/${g.maxPlayers}`),
  );

  let actionsCol: HTMLElement;
  if (isSiege && !full) {
    // Siege rooms are single-team co-op: one JOIN button, everyone on team 1.
    actionsCol = el(
      "button",
      { class: "join-btn", onclick: () => onPlay(g.id, 1) },
      "JOIN",
    );
  } else if (full) {
    actionsCol = el("button", { class: "join-btn full", disabled: true }, "FULL");
  } else if (g.teamCount === 0) {
    actionsCol = el(
      "button",
      { class: "join-btn", onclick: () => onPlay(g.id, 0) },
      "JOIN",
    );
  } else {
    const pills: HTMLElement[] = [];
    for (let i = 1; i <= g.teamCount; i++) {
      const cnt = g.teamCounts[i] ?? 0;
      const color = TEAM_COLORS[i] ?? "#ccc";
      pills.push(
        el(
          "button",
          {
            class: "team-btn",
            style: `--team-color: ${color};`,
            title: `Join Team ${i} (${cnt} players)`,
            onclick: () => onPlay(g.id, i),
          },
          el("span", { class: "team-swatch" }),
          el("span", { class: "team-lbl" }, `T${i}`),
          el("span", { class: "team-cnt" }, String(cnt)),
        ),
      );
    }
    actionsCol = el("div", { class: "team-row" }, ...pills);
  }

  return el("div", { class: "room-row" }, nameCol, countCol, actionsCol);
}

function teamCountPicker(): HTMLSelectElement {
  const sel = document.createElement("select");
  sel.className = "lcard-select";
  const ffa = document.createElement("option");
  ffa.value = "0";
  ffa.textContent = "FFA";
  sel.append(ffa);
  for (let i = 2; i <= MAX_TEAM; i++) {
    const opt = document.createElement("option");
    opt.value = String(i);
    opt.textContent = `${i} TEAMS`;
    if (i === DEFAULT_TEAM_COUNT) opt.selected = true;
    sel.append(opt);
  }
  return sel;
}

function stylePicker(): HTMLSelectElement {
  const sel = document.createElement("select");
  sel.className = "lcard-select";
  for (const [value, label] of [["classic", "CLASSIC"], ["turbo", "SUPER"]] as const) {
    const opt = document.createElement("option");
    opt.value = value;
    opt.textContent = label;
    sel.append(opt);
  }
  return sel;
}

// 0..6 covers the common cases without offering a useless "15 bots" option
// that would crowd out humans. Server caps at MAX_PLAYERS_PER_GAME-1.
function botCountPicker(): HTMLSelectElement {
  const sel = document.createElement("select");
  sel.className = "lcard-select";
  for (let i = 0; i <= 6; i++) {
    const opt = document.createElement("option");
    opt.value = String(i);
    opt.textContent = i === 0 ? "NONE" : String(i);
    sel.append(opt);
  }
  return sel;
}

function mapPicker(maps: MapInfo[]): HTMLSelectElement {
  const sel = document.createElement("select");
  sel.className = "lcard-select";
  populateMapPicker(sel, maps);
  return sel;
}

function populateMapPicker(sel: HTMLSelectElement, maps: MapInfo[]): void {
  sel.replaceChildren();
  const procedural = document.createElement("option");
  procedural.value = "";
  procedural.textContent = "STARTER ISLE";
  sel.append(procedural);
  // RANDOM picks a fresh map at room creation and re-rolls between matches;
  // only meaningful when there's more than one map to pick from.
  if (maps.length > 0) {
    const random = document.createElement("option");
    random.value = RANDOM_MAP_ID;
    random.textContent = "RANDOM";
    sel.append(random);
  }
  for (const m of maps) {
    const opt = document.createElement("option");
    opt.value = m.id;
    opt.textContent = m.name.toUpperCase();
    sel.append(opt);
  }
}
